import { ReactElement, MouseEvent } from "react";
import { Icon } from "client/Components";
import styles from "./DocumentName.module.less";
import {
	FileThumbnailKind,
	getExtensionAndType,
	getSpriteClassForExtension,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/fileExtensions";
import { getFileNameWithoutExtension } from "client_legacy/Documents/path";
import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";

export enum DocumentNameKind {
	Default = 1,
	Bookmark = 2
}

type DefaultMode = {
	kind: DocumentNameKind.Default;
	hasBookmark?: boolean;
	isInfected?: boolean | null;
};

type BookmarkMode = {
	kind: DocumentNameKind.Bookmark;
	extension: string;
};

export type DocumentNameMode =
	| DefaultMode
	| BookmarkMode;

export type DocumentNameProps = {
	name: string;
	documentKind: DocumentItemType;
	mode: DocumentNameMode;
	onClick?: () => void;
	trackingId?: string;
};

export const DocumentName = (props: DocumentNameProps): ReactElement => {
	const {
		name: givenName,
		documentKind,
		mode,
		onClick,
		trackingId
	} = props;

	const { thumbnail, name } = getNameAndThumbnail(
		givenName,
		documentKind,
		mode
	);

	const clickHandler = (args: MouseEvent) => {
		if (onClick) {
			args.preventDefault();
			args.stopPropagation();

			onClick();
		}
	};

	const showBookmarkIcon: boolean = mode.kind === DocumentNameKind.Bookmark
		|| (mode.kind === DocumentNameKind.Default && Boolean(mode.hasBookmark));

	const showInfectedIcon: boolean = mode.kind === DocumentNameKind.Default
		&& Boolean(mode.isInfected);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{ [styles.root_clickable]: Boolean(onClick) }
			)}
			onClick={clickHandler}
		>
			<span className={styles.icon}>
				<Icon
					value={{
						kind: "svg",
						name: thumbnail
					}}
				/>
			</span>

			<span
				{...setTrackingId("name")}
				className={styles.name}
				title={name}
			>
				{name}
			</span>

			{showInfectedIcon && (
				<span className={styles.infected}>
					<Icon value="shield-xmark" />
				</span>
			)}

			{showBookmarkIcon && (
				<span className={styles.bookmark}>
					<Icon value="star-filled" />
				</span>
			)}
		</div>
	);
};

DocumentName.displayName = "DocumentName";

export function getNameAndThumbnail(
	name: string,
	documentKind: DocumentItemType,
	mode: DocumentNameMode,
): { name: string; thumbnail: FileThumbnailKind; } {

	let thumbnail: FileThumbnailKind;
	switch (documentKind) {
		case DocumentItemType.OneNoteNotebook:
			thumbnail = getSpriteClassForExtension(ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION);
			break;
		case DocumentItemType.Folder:
			thumbnail = "folder";
			break;
		case DocumentItemType.File:
			if (mode.kind === DocumentNameKind.Bookmark) {
				thumbnail = getSpriteClassForExtension(mode.extension);
			} else {
				thumbnail = getExtensionAndType(
					name,
					false
				).type;
			}
			break;
		default:
			throw new Error(`Unknown document kind '${documentKind}'`);
	}

	return {
		name:
			documentKind === DocumentItemType.File
				&& mode.kind !== DocumentNameKind.Bookmark
				&& thumbnail !== "file"
					? getFileNameWithoutExtension(name)
					: name,
		thumbnail
	};
}